import React, { useState } from 'react';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Typography, Card, CardContent, Theme } from '@material-ui/core';
import { CreditCard, CheckCircle } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paymentCard: {
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: '8px',
      marginTop: '16px',
      backgroundColor: '#ffffff',
    },
    completedCard: {
      border: `1px solid ${theme.color.primary.light}`,
      borderRadius: '8px',
      marginTop: '16px',
      backgroundColor: `${theme.color.primary.light}10`,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
    },
    icon: {
      marginRight: '12px',
      fontSize: '20px',
    },
    title: {
      fontWeight: 700,
      fontSize: '16px',
      color: theme.color.grey.neutral400,
    },
    productName: {
      fontWeight: 600,
      fontSize: '15px',
      color: theme.color.grey.neutral400,
      marginBottom: '8px',
    },
    amount: {
      fontSize: '14px',
      color: theme.color.grey.neutral300,
      marginBottom: '16px',
    },
    paymentButton: {
      backgroundColor: theme.color.secondary.main900,
      color: '#ffffff',
      borderRadius: '4px',
      padding: '8px 16px',
      fontWeight: 600,
      fontSize: '14px',
      width: '100%',
    },
    completedText: {
      color: theme.color.primary.main,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
    },
    disclaimer: {
      fontSize: '12px',
      color: theme.color.grey.neutral300,
      marginTop: '8px',
      fontStyle: 'italic',
    },
  })
);

interface Props {
  taskId: string;
  productName: string;
  isCompleted: boolean;
  userId?: string;
}

export const StripePaymentTaskComponent: React.FC<Props> = ({ taskId, productName, isCompleted, userId }) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [processing, setProcessing] = useState(false);
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const createPaymentLink = useCampfireFetch({ defer: true });

  const handlePayment = async () => {
    setProcessing(true);

    const currentUrl = window.location.href;
    const successUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment=success`;
    const cancelUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment=cancelled`;

    try {
      const response = await createPaymentLink.run({
        url: `${apiUrl}/wm/task/stripe/create-payment-link`,
        method: 'post',
        data: { taskId, successUrl, cancelUrl, userId },
      });

      // Check if we have the checkout URL in the response data
      if (response.data?.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.checkoutUrl;
      } else if (response.data?.checkoutUrl) {
        // Redirect to Stripe Checkout (fallback for direct structure)
        window.location.href = response.data.checkoutUrl;
      } else {
        setSnackbar({
          open: true,
          message: response.data?.message || 'Failed to create payment link',
          variant: 'error',
        });
        setProcessing(false);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      setSnackbar({
        open: true,
        message: 'Unable to process payment. Please try again.',
        variant: 'error',
      });
      setProcessing(false);
    }
  };

  if (isCompleted) {
    return (
      <Card className={classes.completedCard}>
        <CardContent>
          <Box className={classes.header}>
            <CheckCircle className={classes.icon} style={{ color: theme.color.primary.main }} />
            <Typography className={classes.title}>Payment Completed</Typography>
          </Box>
          <Typography className={classes.completedText}>
            <CheckCircle style={{ fontSize: '16px', marginRight: '6px' }} />
            Payment completed successfully for {productName}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.paymentCard}>
      <CardContent>
        <Box className={classes.header}>
          <CreditCard className={classes.icon} style={{ color: theme.color.secondary.main900 }} />
          <Typography className={classes.title}>Payment Required</Typography>
        </Box>

        <Typography className={classes.productName}>{productName}</Typography>

        <TabletButton
          variant='contained'
          onClick={handlePayment}
          disabled={processing || createPaymentLink.isLoading}
          startIcon={<CreditCard style={{ fontSize: '16px' }} />}
          className={classes.paymentButton}
          style={{
            opacity: processing || createPaymentLink.isLoading ? 0.7 : 1,
          }}
        >
          {processing || createPaymentLink.isLoading ? 'Processing...' : 'Complete Payment'}
        </TabletButton>

        <Typography className={classes.disclaimer}>
          You will be redirected to Stripe&apos;s secure payment page
        </Typography>
      </CardContent>
    </Card>
  );
};
